import { deskPlaced, isOpen } from '@alphadesk/ontology/objects/position.ts';
import type { TooCloseLeg } from '@alphadesk/ontology/order.ts';
import {
  isDesk,
  type Viewer,
  type WireFallback,
  type WirePosition,
} from '@alphadesk/web/contract.ts';
import clsx from 'clsx';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { useStandingFallbacks } from '../lib/fallbacks.ts';
import { fmtPrice } from '../lib/format.ts';

interface FallbacksState {
  /** False until Zerodha was asked; until then an absent fallback stop means nothing. */
  readable: boolean;
  byPosition: ReadonlyMap<number, WireFallback>;
  /** Arming sends no socket frame, so it must re-ask. */
  refresh: () => void;
}

const NOBODY: FallbacksState = {
  readable: false,
  byPosition: new Map(),
  refresh: () => {},
};
const FallbacksContext = createContext<FallbacksState>(NOBODY);

export function FallbacksProvider({ viewer, children }: { viewer: Viewer; children: ReactNode }) {
  const { answer, refresh } = useStandingFallbacks(!isDesk(viewer));
  const value = useMemo<FallbacksState>(
    () => ({
      readable: answer.readable,
      byPosition: new Map(answer.fallbacks.map((fallback) => [fallback.positionId, fallback])),
      refresh,
    }),
    [answer, refresh],
  );
  return <FallbacksContext.Provider value={value}>{children}</FallbacksContext.Provider>;
}

export function useFallbacksRefresh(): () => void {
  return useContext(FallbacksContext).refresh;
}

export interface FallbackView {
  recorded: number;
  standing: number;
  asked: boolean;
}

const UNASKED: FallbackView = { recorded: 0, standing: 0, asked: false };

/** Merged fills each park their own fallback, so a row spans several ids. */
export function useFallback(position: WirePosition, fillIds: readonly number[]): FallbackView {
  const { readable, byPosition } = useContext(FallbacksContext);
  return useMemo(() => {
    if (!readable || !isOpen(position.status)) return UNASKED;
    const found = fillIds
      .map((id) => byPosition.get(id))
      .filter((found): found is WireFallback => found !== undefined);
    return {
      recorded: found.length,
      standing: found.filter((one) => one.standing).length,
      asked: true,
    };
  }, [readable, byPosition, position, fillIds]);
}

/**
 * Of the fills the desk placed and still holds, how many have a fallback stop standing at the
 * broker; null until Zerodha was asked, or where the desk placed nothing held.
 */
export function useFallbackTally(
  positions: readonly WirePosition[],
): { standing: number; of: number } | null {
  const { readable, byPosition } = useContext(FallbacksContext);
  return useMemo(() => {
    if (!readable) return null;
    const placed = positions.filter(
      (position) => isOpen(position.status) && deskPlaced(position.entry?.status),
    );
    if (placed.length === 0) return null;
    return {
      standing: placed.filter((position) => byPosition.get(position.id)?.standing === true).length,
      of: placed.length,
    };
  }, [readable, byPosition, positions]);
}

export function fallbackSpeaks(fallback: FallbackView): boolean {
  return fallback.asked && fallback.recorded > fallback.standing;
}

/**
 * - `no-risk` — the analyst's stop sits on or through their entry, so there is no R to
 *   measure a stop in. Passes the moment they move the stop behind the entry again.
 * - `too-close` — Kite's band around the market. Passes by itself as the price moves clear;
 *   still reachable despite the ticket's check, since a move in the analyst's R re-parks it.
 */
export type Unparkable = { why: 'no-risk' } | { why: 'too-close'; near: TooCloseLeg };

export function unparkableLeg(
  position: WirePosition,
  fallback: FallbackView,
  leg: { fallback: number | null; tooClose: TooCloseLeg | null } | null,
): Unparkable | null {
  if (!deskPlaced(position.entry?.status) || !isOpen(position.status)) return null;
  // Read off the leg alone: the button is hidden before Zerodha has answered.
  if (leg && leg.fallback === null) return { why: 'no-risk' };
  if (!fallback.asked || fallback.standing > 0) return null;
  return leg?.tooClose ? { why: 'too-close', near: leg.tooClose } : null;
}

const deskStillExits = (position: WirePosition): string =>
  position.exits !== 'desk'
    ? 'Order placing is off, so nothing else of mine stands behind this position.'
    : position.exitsWithRecommendation
      ? 'I still square this position off when the recommendation ends: at its target on a touch, at its stop on a five-minute close.'
      : 'When the recommendation ends, I ask you to exit it.';

export function FallbackNotes({
  fallback,
  position,
  unparkable = null,
}: {
  fallback: FallbackView;
  position: WirePosition;
  unparkable?: Unparkable | null;
}) {
  return (
    <>
      {unparkable?.why === 'no-risk' &&
        (fallback.standing > 0 ? (
          <NotePill
            title={`The analyst’s stop sits on their entry, so there is no R to measure a stop in: the fallback stop at your broker is left where it stood. ${deskStillExits(
              position,
            )} It is moved again the moment they move the stop behind their entry.`}
          >
            fallback left where it stood while their stop is at entry
          </NotePill>
        ) : (
          <NotePill
            title={`The analyst’s stop sits on their entry, so there is no R to measure a stop in. ${deskStillExits(
              position,
            )} One goes back the moment they move the stop behind their entry again.`}
          >
            no fallback stop while their stop is at entry
          </NotePill>
        ))}
      {unparkable?.why === 'too-close' && (
        <NotePill
          title={`The fallback stop for this position sits ${fmtPrice(
            unparkable.near.away,
          )} from the market, and your broker holds no stop inside ${fmtPrice(
            unparkable.near.needed,
          )} of the price. I still square this position off if the recommendation stops out on a five-minute close; what is missing is the stop behind that, for a gap or an outage. It parks by itself once the price moves clear.`}
        >
          Your broker won’t hold a stop this close
        </NotePill>
      )}
      {fallbackSpeaks(fallback) && (
        <NotePill
          title={`The fallback stop I parked at your broker is no longer standing — fired, or deleted there. ${deskStillExits(
            position,
          )} Re-park fallback puts a fresh one back, for a gap or an outage.`}
        >
          fallback gone at your broker
        </NotePill>
      )}
    </>
  );
}

export function NotePill({
  title,
  onClick,
  children,
}: {
  title?: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  const dress = 'tnum flex items-baseline gap-2 border-l-2 border-warn pl-2 text-xs text-ink-muted';
  if (!onClick) {
    return (
      <span title={title} className={dress}>
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={clsx(dress, 'text-left transition-opacity hover:opacity-90 touch:min-h-7')}
    >
      {children}
    </button>
  );
}
